import { Controller } from "@hotwired/stimulus";

/**
* This controller is used to autofocus an input element or textarea and move cursor to the end if it's a text field
* @markup-example:

<input type="text" name="name" data-controller="autofocus" />
<textarea name="description" data-controller="autofocus"></textarea>
**/

export default class extends Controller {
  connect(){
    // If already visible, focus immediately; otherwise wait until visible
    if (this._isVisible()) {
      this._focusToEnd()
    } else {
      this._deferUntilVisible()
    }
  }

  disconnect(){
    this._teardownDeferrers()
  }

  // Private
  _focusToEnd(){
    this.element.focus()

    // If it's an input element that can have text content or a textarea
    if ((this.element.tagName === 'INPUT' &&
         ['text', 'password', 'email', 'search', 'tel', 'url'].includes(this.element.type)) ||
        this.element.tagName === 'TEXTAREA') {
      const length = this.element.value.length
      try {
        this.element.setSelectionRange(length, length)
      } catch (_) {
        // Some input types do not support selection ranges; ignore
      }
    }
  }

  _isVisible(){
    const rects = this.element.getClientRects()
    return rects.length > 0 && this.element.offsetParent !== null
  }

  _deferUntilVisible(){
    // IntersectionObserver is the preferred way
    if ('IntersectionObserver' in window) {
      this._intersectionObserver = new IntersectionObserver((entries) => {
        const entry = entries[0]
        if (entry && entry.isIntersecting) {
          this._focusToEnd()
          this._teardownDeferrers()
        }
      }, { threshold: 0.01 })
      this._intersectionObserver.observe(this.element)
    }

    // Also watch for DOM mutations that might reveal the element (e.g., class changes when switching tabs)
    if ('MutationObserver' in window) {
      this._mutationObserver = new MutationObserver(() => {
        if (this._isVisible()) {
          this._focusToEnd()
          this._teardownDeferrers()
        }
      })
      this._mutationObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] })
    }

    // Fallback: try after a short delay
    this._visibilityTimeout = window.setTimeout(() => {
      if (this._isVisible()) this._focusToEnd()
      this._teardownDeferrers()
    }, 1000)
  }

  _teardownDeferrers(){
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect()
      this._intersectionObserver = null
    }
    if (this._mutationObserver) {
      this._mutationObserver.disconnect()
      this._mutationObserver = null
    }
    if (this._visibilityTimeout) {
      window.clearTimeout(this._visibilityTimeout)
      this._visibilityTimeout = null
    }
  }
}
